import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { classifyDocument, classifyDocumentEnhanced, uploadDocument, classifyParagraphs, uploadAndClassifyParagraphs, formatClassifiedDocument, runAdversarialTest, classifyCompare, exportClassificationReport, classifyBulk, getBulkStatus, submitFeedback, getClassificationMetadata } from '../services/api';
import FileUpload from '../components/FileUpload';
import ClassificationBadge from '../components/ClassificationBadge';
import StructuredClassificationDetails from '../components/StructuredClassificationDetails';
import PortionMarkedDocument from '../components/PortionMarkedDocument';
import ProcessingStatus from '../components/ProcessingStatus';
import AuthorityReviewFlag from '../components/AuthorityReviewFlag';
import SCGRuleLink from '../components/SCGRuleLink';
import ViewInPdfButton from '../components/ViewInPdfButton';
import { DocumentIcon, ClockIcon, CheckCircleIcon, ExclamationCircleIcon, ScissorsIcon, Squares2X2Icon, ShieldExclamationIcon, ChartBarIcon, ArrowUpTrayIcon, XCircleIcon } from '@heroicons/react/24/outline';
import MosaicDetection from './MosaicDetection';
import ConfidenceHeatmap from '../components/ConfidenceHeatmap';

export default function ClassifyDocument() {
  const navigate = useNavigate();
  const location = useLocation();
  const [textInput, setTextInput] = useState('');
  const useScgRules = true; // Always use SCG rules
  const [useParagraphMode, setUseParagraphMode] = useState(true);
  const [useEnhancement, setUseEnhancement] = useState(true);
  const [retrievalMode, setRetrievalMode] = useState(''); // '' = system default
  const useSemanticRetrieval = true; // Always enabled
  const [useLlmSegmentation, setUseLlmSegmentation] = useState(false);
  const [classificationResult, setClassificationResult] = useState(null);
  const [paragraphResult, setParagraphResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [activeTab, setActiveTab] = useState('text'); // 'text', 'file', 'mosaic', or 'bulk'
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [adversarialResults, setAdversarialResults] = useState({});  // keyed by 'whole' or paragraph index
  const [adversarialLoadingKeys, setAdversarialLoadingKeys] = useState(new Set());
  const [useAdversarialTest, setUseAdversarialTest] = useState(false);
  const [restoredFromHistory, setRestoredFromHistory] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [compareResults, setCompareResults] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  // Feedback state
  const [feedbackDecision, setFeedbackDecision] = useState(null); // 'accept', 'reject', 'override'
  const [feedbackReasoning, setFeedbackReasoning] = useState('');
  const [feedbackOverride, setFeedbackOverride] = useState({ level: '', controls: '', compartments: '' });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Restore state from query history if navigated with restoredQuery
  useEffect(() => {
    const restoredQuery = location.state?.restoredQuery;
    if (restoredQuery && restoredQuery.query_type === 'classification') {
      // Restore the input text
      if (restoredQuery.input_text) {
        setTextInput(restoredQuery.input_text);
      }

      // Restore input parameters
      if (restoredQuery.input_params) {
        if (restoredQuery.input_params.useParagraphMode !== undefined) {
          setUseParagraphMode(restoredQuery.input_params.useParagraphMode);
        }
        if (restoredQuery.input_params.useEnhancement !== undefined) {
          setUseEnhancement(restoredQuery.input_params.useEnhancement);
        }
        if (restoredQuery.input_params.retrieval_mode) {
          setRetrievalMode(restoredQuery.input_params.retrieval_mode);
        }
        if (restoredQuery.input_params.useLlmSegmentation !== undefined) {
          setUseLlmSegmentation(restoredQuery.input_params.useLlmSegmentation);
        }
      }

      // Restore results
      if (restoredQuery.result) {
        // Check if this is a paragraph result (has 'paragraphs' array)
        if (restoredQuery.result.paragraphs && Array.isArray(restoredQuery.result.paragraphs)) {
          setParagraphResult(restoredQuery.result);
          setClassificationResult(null);
        } else {
          setClassificationResult(restoredQuery.result);
          setParagraphResult(null);
        }
      }

      setRestoredFromHistory(true);
      setActiveTab('text');

      // Clear the location state to prevent re-restoration on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-run adversarial test when classification completes and toggle is on
  useEffect(() => {
    if (!useAdversarialTest) return;
    if (classificationResult && !adversarialResults['whole'] && !adversarialLoadingKeys.has('whole')) {
      runAdversarialForKey('whole', classificationResult.classification, classificationResult.confidence, textInput, classificationResult.raw_response || classificationResult.reasoning || '', (classificationResult.retrieved_rules || []).map(r => r.rule_id).filter(Boolean), null);
    }
    if (paragraphResult?.paragraphs && paragraphResult.paragraphs.length > 0) {
      // Build sibling context for cross-paragraph consistency checks
      const siblings = paragraphResult.paragraphs.map((p, idx) => ({
        paragraph_number: idx + 1,
        classification: p.full_classification || p.classification || 'UNKNOWN',
        confidence: p.confidence || 0,
      }));
      paragraphResult.paragraphs.forEach((p, i) => {
        if (!adversarialResults[i] && !adversarialLoadingKeys.has(i)) {
          // Extract SCG rule IDs from scg_citations (upload results) or retrieved_rules (text results)
          const ruleIds = (p.retrieved_rules || []).map(r => r.rule_id).filter(Boolean);
          // Also try to extract rule IDs from scg_citations strings
          if (ruleIds.length === 0 && p.scg_citations?.length > 0) {
            const rulePattern = /SCG-[A-Z0-9-]+/g;
            p.scg_citations.forEach(c => {
              const matches = (c || '').match(rulePattern);
              if (matches) ruleIds.push(...matches);
            });
          }
          // Also extract from raw_response if still empty
          if (ruleIds.length === 0 && p.raw_response) {
            const rulePattern = /SCG-[A-Z0-9-]+/g;
            const matches = p.raw_response.match(rulePattern);
            if (matches) ruleIds.push(...[...new Set(matches)]);
          }
          runAdversarialForKey(i, p.classification, p.confidence, p.text || '', p.raw_response || '', ruleIds, siblings);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationResult, paragraphResult, useAdversarialTest]);

  // Poll bulk classification batch status
  useEffect(() => {
    if (!batchId || batchStatus?.status === 'completed') return;
    const interval = setInterval(async () => {
      try {
        const status = await getBulkStatus(batchId);
        setBatchStatus(status);
        if (status.status === 'completed' || status.status === 'failed') clearInterval(interval);
      } catch (err) {
        console.error('Bulk status poll error:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, batchStatus?.status]);

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBatchStatus(null);
    try {
      const result = await classifyBulk(bulkFiles);
      setBatchId(result.batch_id);
      setBatchStatus(result);
    } catch (err) {
      console.error('Bulk upload error:', err);
      setBatchStatus({ status: 'failed', error: err.message || 'Upload failed' });
    } finally {
      setBulkUploading(false);
    }
  };

  const classifyMutation = useMutation({
    mutationFn: ({ text, options }) => {
      return classifyDocumentEnhanced(text, options);
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setClassificationResult(null);
      setParagraphResult(null);
      resetFeedback();
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Analyzing document content...');
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 500);
      
      // Store interval for cleanup
      window.classifyProgressInterval = progressInterval;
      
      // Update messages over time
      setTimeout(() => setLoadingMessage('Retrieving SCG rules...'), 1000);
      setTimeout(() => setLoadingMessage('Applying classification logic...'), 2000);
      setTimeout(() => setLoadingMessage('Finalizing results...'), 3000);
    },
    onSuccess: (data) => {
      // Clean up progress interval
      if (window.classifyProgressInterval) {
        clearInterval(window.classifyProgressInterval);
        window.classifyProgressInterval = null;
      }
      
      setLoadingProgress(100);
      // Minimum loading time of 2 seconds to ensure animation is visible
      setTimeout(() => {
        setClassificationResult(data);
        setLoadingProgress(0); // Reset progress
        setLoadingMessage('Processing...'); // Reset message
        setIsLocalLoading(false);
      }, 2000);
    },
    onError: () => {
      // Clean up progress interval
      if (window.classifyProgressInterval) {
        clearInterval(window.classifyProgressInterval);
        window.classifyProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const paragraphMutation = useMutation({
    mutationFn: ({ text, options }) => {
      return classifyParagraphs(text, options);
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setClassificationResult(null);
      setParagraphResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(5);
      setLoadingMessage('Breaking document into paragraphs...');
      
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + Math.random() * 10;
        });
      }, 800);
      
      // Store interval for cleanup
      window.paragraphProgressInterval = progressInterval;
      
      // Update messages over time
      setTimeout(() => setLoadingMessage('Classifying each paragraph...'), 1500);
      setTimeout(() => setLoadingMessage('Determining overall classification...'), 3000);
    },
    onSuccess: (data) => {
      // Clean up progress interval
      if (window.paragraphProgressInterval) {
        clearInterval(window.paragraphProgressInterval);
        window.paragraphProgressInterval = null;
      }
      
      setLoadingProgress(100);
      // Minimum loading time of 2 seconds to ensure animation is visible
      setTimeout(() => {
        setParagraphResult(data);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 2000);
    },
    onError: () => {
      // Clean up progress interval
      if (window.paragraphProgressInterval) {
        clearInterval(window.paragraphProgressInterval);
        window.paragraphProgressInterval = null;
      }
      
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      // Always use the enhanced upload and classify endpoint
      return uploadAndClassifyParagraphs(file, {
        useScgRules,
        chunkSize: useParagraphMode ? 1500 : 2000,
        useLlmSegmentation,
        retrievalMode: retrievalMode || null,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            // Upload is typically ~50% of the work; classification is the other ~50%
            const uploadPct = Math.round((progressEvent.loaded * 50) / progressEvent.total);
            setLoadingProgress(uploadPct);
            if (uploadPct >= 50) {
              setLoadingMessage('Running classification analysis...');
            }
          }
        }
      });
    },
    onMutate: () => {
      // Clear previous results and start loading immediately
      setUploadResult(null);
      setIsLocalLoading(true);
      setLoadingProgress(5);
      setLoadingMessage('Uploading document...');
    },
    onSuccess: (data) => {
      setLoadingProgress(100);
      setLoadingMessage('Classification complete!');
      // Brief delay so user can see completion state
      setTimeout(() => {
        // Always treat as paragraph result since we're using enhanced endpoint
        setParagraphResult(data);
        setUploadResult(null);
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 500);
    },
    onError: () => {
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const formattedDocumentMutation = useMutation({
    mutationFn: ({ file, classificationResults }) => {
      return formatClassifiedDocument(file, classificationResults);
    },
    onMutate: () => {
      setIsLocalLoading(true);
      setLoadingProgress(10);
      setLoadingMessage('Generating formatted document...');
    },
    onSuccess: (response) => {
      setLoadingProgress(100);
      setLoadingMessage('Download ready!');
      
      // Create download link
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'classified_document';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setTimeout(() => {
        setLoadingProgress(0);
        setLoadingMessage('Processing...');
        setIsLocalLoading(false);
      }, 1000);
    },
    onError: () => {
      setLoadingProgress(0);
      setLoadingMessage('Processing...');
      setIsLocalLoading(false);
    }
  });

  const handleTextClassification = async (e) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    
    setClassificationResult(null);
    setParagraphResult(null);
    setAdversarialResults({});
    setAdversarialLoadingKeys(new Set());

    if (useParagraphMode) {
      paragraphMutation.mutate({
        text: textInput,
        options: {
          useScgRules,
          useHyde: false, // Disable enhancement for paragraph mode for performance
          useSemanticRetrieval,
          retrievalMode: retrievalMode || null
        }
      });
    } else {
      classifyMutation.mutate({
        text: textInput,
        options: {
          useScgRules,
          useHyde: useEnhancement,
          useSemanticRetrieval,
          retrievalMode: retrievalMode || null
        }
      });
    }
  };

  const handleFileUpload = async (file) => {
    setUploadResult(null);
    uploadMutation.mutate(file);
  };

  const runAdversarialForKey = async (key, classification, confidence, text, reasoning, scgRules, siblings) => {
    setAdversarialLoadingKeys(prev => new Set([...prev, key]));
    try {
      const payload = {
        text: text || '',
        classification: classification || 'UNCLASSIFIED',
        confidence: confidence || 0,
        reasoning: reasoning || '',
        scg_rules_cited: scgRules || [],
      };
      // Include sibling paragraph classifications for cross-paragraph consistency
      if (siblings && siblings.length > 1) {
        payload.sibling_classifications = siblings;
      }
      const data = await runAdversarialTest(payload);
      setAdversarialResults(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      setAdversarialResults(prev => ({ ...prev, [key]: { error: err.message || 'Adversarial test failed' } }));
    } finally {
      setAdversarialLoadingKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const handleTearlineRedirect = () => {
    // Store the current text in sessionStorage so it can be used in Tearline
    if (textInput.trim()) {
      sessionStorage.setItem('tearlineText', textInput);
      navigate('/tearline');
    }
  };

  const handleCompare = async () => {
    setCompareLoading(true);
    setCompareResults(null);
    setCompareError(null);
    try {
      const data = await classifyCompare(textInput);
      setCompareResults(data);
    } catch (err) {
      console.error('Compare failed:', err);
      setCompareError(err.response?.data?.detail || 'Failed to compare retrieval modes. Please try again.');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleExportReport = async () => {
    const exportResult = classificationResult || paragraphResult;
    if (!exportResult) return;
    setExportError(null);
    try {
      const blob = await exportClassificationReport(exportResult);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'classification_report.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError(err.response?.data?.detail || 'Failed to export PDF report. Please try again.');
    }
  };

  const renderAdversarialPanel = (key, originalConfidence) => {
    const advResult = adversarialResults[key];
    const isLoading = adversarialLoadingKeys.has(key);

    return (
      <div className="mt-4 pt-3 border-t border-[var(--vault-border)]">
        <div className="flex items-center gap-2 mb-2">
          <ShieldExclamationIcon className="w-4 h-4 text-[var(--spotlight-gold)]" />
          <span className="text-xs font-semibold tracking-wider uppercase text-[var(--text-primary)]">Adversarial Self-Test</span>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <svg className="animate-spin h-3 w-3 text-[var(--spotlight-gold)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Running counter-argument analysis...
          </div>
        )}

        {advResult && !advResult.error && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`stamp text-xs ${
                advResult.recommendation === 'AFFIRM' ? 'stamp-unclassified' :
                advResult.recommendation === 'CHALLENGE' ? 'stamp-secret' :
                'stamp-confidential'
              }`}>{advResult.recommendation}</span>
              <div className="flex items-center gap-3 text-xs">
                {originalConfidence != null && (
                  <span className="text-[var(--text-muted)]">Original: <strong className="text-[var(--text-primary)]">{(originalConfidence * 100).toFixed(0)}%</strong></span>
                )}
                <span className="text-[var(--text-muted)]">Calibrated: <strong className={`${
                  advResult.calibrated_confidence >= (originalConfidence || 0) ? 'text-[var(--success)]' : 'text-[var(--warning)]'
                }`}>{(advResult.calibrated_confidence * 100).toFixed(0)}%</strong></span>
                <span className="text-[var(--text-muted)]">{advResult.processing_time}s</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Over-Class Risk</div>
                <span className={`text-xs font-bold ${advResult.over_classification_risk === 'HIGH' ? 'text-[var(--error)]' : advResult.over_classification_risk === 'MEDIUM' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{advResult.over_classification_risk}</span>
              </div>
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Under-Class Risk</div>
                <span className={`text-xs font-bold ${advResult.under_classification_risk === 'HIGH' ? 'text-[var(--error)]' : advResult.under_classification_risk === 'MEDIUM' ? 'text-[var(--warning)]' : 'text-[var(--success)]'}`}>{advResult.under_classification_risk}</span>
              </div>
            </div>
            {advResult.overall_assessment && (
              <div className="vault-card p-2">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Assessment</div>
                <p className="text-xs text-[var(--text-secondary)]">{advResult.overall_assessment}</p>
              </div>
            )}
            {advResult.counter_arguments?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Counter Arguments</div>
                <ul className="space-y-1">
                  {advResult.counter_arguments.map((arg, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                      <span className="text-[var(--error)] mt-0.5 shrink-0">&#x2716;</span>
                      <span>{arg}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {advResult.weak_points?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Weak Points</div>
                <ul className="space-y-1">
                  {advResult.weak_points.map((wp, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-secondary)]">
                      <span className="text-[var(--warning)] mt-0.5 shrink-0">&#x26A0;</span>
                      <span>{wp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {advResult?.error && (
          <div className="text-xs text-[var(--error)]">Error: {advResult.error}</div>
        )}
      </div>
    );
  };

  // Reset feedback state when new classification runs
  const resetFeedback = () => {
    setFeedbackDecision(null);
    setFeedbackReasoning('');
    setFeedbackOverride({ level: '', controls: '', compartments: '' });
    setFeedbackSubmitted(false);
    setFeedbackSubmitting(false);
  };

  const handleFeedbackSubmit = async (result) => {
    if (!feedbackDecision || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    try {
      const entry = {
        pipeline_type: 'classification',
        document_text: textInput || '',
        decision: feedbackDecision,
        user_reasoning: feedbackReasoning || null,
        original_value: {
          level: result.classification,
          controls: result.caveats || [],
          compartments: result.compartments || [],
          full_classification: result.full_classification,
        },
        corrected_value: feedbackDecision === 'override' ? {
          level: feedbackOverride.level || result.classification,
          controls: feedbackOverride.controls || result.caveats?.join(', ') || '',
          compartments: feedbackOverride.compartments || result.compartments?.join(', ') || '',
        } : null,
        confidence: result.confidence,
        metadata: result.provenance_decision_id
          ? { source_decision_id: result.provenance_decision_id }
          : undefined,
      };
      await submitFeedback([entry]);
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const renderFeedbackPanel = (result) => {
    if (!result) return null;

    if (feedbackSubmitted) {
      return (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center text-sm text-green-700">
            <CheckCircleIcon className="w-5 h-5 mr-2" />
            Feedback recorded — {feedbackDecision === 'accept' ? 'classification confirmed' : feedbackDecision === 'override' ? 'correction saved' : 'rejection noted'}. This will improve future classifications.
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Classification Feedback</h4>

        {/* Decision buttons */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setFeedbackDecision('accept')}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              feedbackDecision === 'accept'
                ? 'bg-green-100 border-green-400 text-green-800'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-green-50'
            }`}
          >
            ✓ Correct
          </button>
          <button
            onClick={() => setFeedbackDecision('reject')}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              feedbackDecision === 'reject'
                ? 'bg-red-100 border-red-400 text-red-800'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-red-50'
            }`}
          >
            ✗ Incorrect
          </button>
          <button
            onClick={() => setFeedbackDecision('override')}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              feedbackDecision === 'override'
                ? 'bg-amber-100 border-amber-400 text-amber-800'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-amber-50'
            }`}
          >
            ↑ Override
          </button>
        </div>

        {/* Override fields */}
        {feedbackDecision === 'override' && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Correct Level</label>
              <select
                value={feedbackOverride.level}
                onChange={(e) => setFeedbackOverride(prev => ({ ...prev, level: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="">— keep —</option>
                <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                <option value="SECRET">SECRET</option>
                <option value="TOP SECRET">TOP SECRET</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Correct Controls</label>
              <input
                type="text"
                placeholder="e.g. SI, TK"
                value={feedbackOverride.controls}
                onChange={(e) => setFeedbackOverride(prev => ({ ...prev, controls: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Correct Compartments</label>
              <input
                type="text"
                placeholder="e.g. NOFORN"
                value={feedbackOverride.compartments}
                onChange={(e) => setFeedbackOverride(prev => ({ ...prev, compartments: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1"
              />
            </div>
          </div>
        )}

        {/* Reasoning */}
        {feedbackDecision && feedbackDecision !== 'accept' && (
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Reasoning (optional)</label>
            <textarea
              value={feedbackReasoning}
              onChange={(e) => setFeedbackReasoning(e.target.value)}
              placeholder="Why is this classification wrong?"
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 h-16 resize-none"
            />
          </div>
        )}

        {/* Submit */}
        {feedbackDecision && (
          <button
            onClick={() => handleFeedbackSubmit(result)}
            disabled={feedbackSubmitting}
            className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        )}
      </div>
    );
  };

  const renderResult = (result, type = 'classification') => {
    if (!result) return null;

    return (
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              {type === 'upload' ? 'Document Uploaded & Classified' : 'Classification Result'}
            </h3>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View Provenance Button */}
            {result?.provenance_decision_id && (
              <a
                href={`/provenance/${encodeURIComponent(result.provenance_decision_id)}`}
                className="px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                title="See every rule retrieved vs cited for this decision"
              >
                View provenance
              </a>
            )}
            {/* Export PDF Report Button */}
            <button
              onClick={handleExportReport}
              className="px-3 py-1.5 text-sm text-green-400 bg-green-900/30 border border-green-700 rounded-md hover:bg-green-900/50"
            >
              Export PDF Report
            </button>
            {/* Tearline Button */}
            {textInput.trim() && type === 'classification' && (
              <button
                onClick={handleTearlineRedirect}
                className="inline-flex items-center px-3 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ScissorsIcon className="w-4 h-4 mr-2" />
                Tearline
              </button>
            )}
          </div>
        </div>

        {/* Classification Feedback — promoted to top so analysts see it immediately */}
        {type === 'classification' && renderFeedbackPanel(result)}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classification Level
            </label>
            <div className="space-y-2">
              <ClassificationBadge 
                classification={result.classification}
                fullClassification={result.full_classification}
                caveats={result.caveats}
                releasability={result.releasability}
                confidence={result.confidence}
              />
              <AuthorityReviewFlag 
                reasoning={result.raw_response}
                contradictions={result.contradictions}
                variant="compact" 
              />
            </div>
          </div>
          
          {result.extraction_method && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extraction Method
              </label>
              <span className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-blue-100 text-blue-800">
                {result.extraction_method}
              </span>
            </div>
          )}
          
          {result.word_count && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Word Count
              </label>
              <span className="text-sm text-gray-900">{result.word_count} words</span>
            </div>
          )}
          
          {result.document_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document ID
              </label>
              <span className="text-sm font-mono text-gray-900">{result.document_id}</span>
            </div>
          )}
        </div>
        
        {(result.details || result.raw_response) && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classification Details
            </label>
            <StructuredClassificationDetails result={result} />
          </div>
        )}
        
        {/* Enhancement Metadata */}
        {result.enhancement_metadata && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enhancement Information
            </label>
            <div className="bg-blue-50 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-blue-900">Processing Time:</span>
                <span className="text-blue-700">{(result.enhancement_metadata.processing_time || 0).toFixed(2)}s</span>
              </div>
              
              {result.enhancement_metadata.enhancement_methods && result.enhancement_metadata.enhancement_methods.length > 0 && (
                <div className="text-xs">
                  <span className="font-medium text-blue-900">Enhancement Methods:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.enhancement_metadata.enhancement_methods.map((method, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                        {method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-xs">
                {result.enhancement_metadata.num_hypothetical_entries > 0 && (
                  <div>
                    <span className="font-medium text-blue-900">Enhanced Entries:</span>
                    <span className="ml-1 text-blue-700">{result.enhancement_metadata.num_hypothetical_entries}</span>
                  </div>
                )}
                
                {result.enhancement_metadata.num_retrieved_rules > 0 && (
                  <div>
                    <span className="font-medium text-blue-900">SCG Rules:</span>
                    <span className="ml-1 text-blue-700">{result.enhancement_metadata.num_retrieved_rules}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Retrieved Context Preview */}
        {result.retrieved_rules && result.retrieved_rules.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Retrieved SCG Rules
            </label>
            <div className="bg-green-50 rounded-md p-3 space-y-2">
              {result.retrieved_rules.slice(0, 5).map((rule, index) => {
                const rid = rule.id || rule.rule_id || 'unknown';
                return (
                  <div key={index} className="text-xs border-l-2 border-green-200 pl-2">
                    <div className="font-medium text-green-900 flex items-center gap-2">
                      <SCGRuleLink ruleId={rid} />
                      <ViewInPdfButton ruleId={rid} variant="icon" size="sm" />
                    </div>
                    <div className="text-green-700">{rule.classification_level} - {rule.item || rule.description || 'No description'}</div>
                    {rule.semantic_similarity != null && (
                      <div className="text-green-600">Similarity: {(rule.semantic_similarity * 100).toFixed(1)}%</div>
                    )}
                  </div>
                );
              })}
              {result.retrieved_rules.length > 5 && (
                <div className="text-xs text-green-600">
                  ... and {result.retrieved_rules.length - 5} more rules
                </div>
              )}
            </div>
          </div>
        )}

        {/* Adversarial Self-Test */}
        {useAdversarialTest && (adversarialResults['whole'] || adversarialLoadingKeys.has('whole')) && renderAdversarialPanel('whole', result.confidence)}
      </div>
    );
  };

  const renderParagraphResults = (result) => {
    if (!result) return null;

    // Build a synthetic result object for the feedback panel from paragraph data
    const overallResult = {
      classification: result.document_classification || result.overall_classification,
      full_classification: result.full_classification,
      caveats: result.caveats,
      compartments: result.compartments,
      confidence: result.document_confidence || result.overall_confidence,
    };

    // If we have portion_marked_document, use the new component
    if (result.portion_marked_document || result.reasoning) {
      return (
        <div className="mt-6">
          {/* Feedback panel at TOP so analysts see it immediately */}
          {renderFeedbackPanel(overallResult)}
          <PortionMarkedDocument
            paragraphResult={result}
            formattedDocumentMutation={formattedDocumentMutation}
            adversarialResults={adversarialResults}
            adversarialLoadingKeys={adversarialLoadingKeys}
            useAdversarialTest={useAdversarialTest}
          />
        </div>
      );
    }

    // Fallback to legacy paragraph display
    return (
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">
            Paragraph-Level Classification Results
          </h3>
        </div>

        {/* Feedback panel at TOP so analysts see it immediately */}
        {renderFeedbackPanel(overallResult)}

        {/* Overall Document Classification */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6 mt-4">
          <h4 className="text-md font-medium text-gray-900 mb-2">Overall Document Classification</h4>
          <div className="flex items-center space-x-4 mb-2">
            <ClassificationBadge 
              classification={result.document_classification}
              fullClassification={result.full_classification}
              caveats={result.caveats}
              releasability={result.releasability}
              confidence={result.document_confidence}
            />
            <span className="text-sm text-gray-600">
              Based on {result.total_paragraphs} paragraphs
            </span>
          </div>
          <AuthorityReviewFlag 
            reasoning={result.reasoning}
            contradictions={result.contradictions}
            variant="compact" 
          />
        </div>

        {/* Individual Paragraph Results */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Individual Paragraph Classifications</h4>
          <div className="space-y-4">
            {result.paragraphs.map((paragraph, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-500">
                      Paragraph {paragraph.paragraph_number}
                    </span>
                    <ClassificationBadge 
                      classification={paragraph.classification}
                      fullClassification={paragraph.full_classification}
                      caveats={paragraph.caveats}
                      releasability={paragraph.releasability}
                      confidence={paragraph.confidence}
                      className="text-xs"
                    />
                    <AuthorityReviewFlag 
                      reasoning={paragraph.raw_response} 
                      contradictions={paragraph.contradictions}
                      variant="compact" 
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded p-3 mb-2">
                  <p className="text-sm text-gray-700 line-clamp-3">
                    {paragraph.text}
                  </p>
                </div>
                
                {paragraph.raw_response && paragraph.raw_response !== paragraph.classification && (
                  <div className="text-xs text-gray-500">
                    <strong>Reasoning:</strong> {paragraph.raw_response}
                  </div>
                )}

                {/* Inline adversarial results per paragraph */}
                {useAdversarialTest && (adversarialResults[index] || adversarialLoadingKeys.has(index)) && (
                  renderAdversarialPanel(index, paragraph.confidence)
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    );
  };

  const isLoading = isLocalLoading || classifyMutation.isLoading || paragraphMutation.isLoading || uploadMutation.isLoading;
  const currentResult = classificationResult || paragraphResult || uploadResult;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Processing Status Banner */}
      <ProcessingStatus
        isProcessing={isLoading}
        progress={loadingProgress}
        message={loadingMessage}
      />
      
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">Classify Documents</h1>
        <p className="mt-2 text-sm font-mono text-[var(--text-muted)]">
          Upload documents or enter text to automatically determine their security classification level
        </p>
      </div>

      {/* Results Section - Always at Top When Present */}
      {currentResult && (
        <div className="mb-8">
          {exportError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{exportError}</p>
                </div>
              </div>
            </div>
          )}
          {/* Confidence Heatmap — auto-shown at TOP for paragraph results so analysts see it immediately */}
          {paragraphResult && paragraphResult.paragraphs && paragraphResult.paragraphs.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowHeatmap(prev => !prev)}
                className={`vault-btn ${showHeatmap ? 'vault-btn-primary' : 'vault-btn-secondary'} text-xs`}
              >
                <ChartBarIcon className="w-4 h-4" />
                {showHeatmap ? 'Hide Confidence Heatmap' : 'Show Confidence Heatmap'}
              </button>

              {showHeatmap && (
                <div className="mt-3 vault-card p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <ChartBarIcon className="w-5 h-5 text-[var(--spotlight-gold)]" />
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)]">
                      Paragraph Confidence Heatmap
                    </h3>
                    <span className="text-xs text-[var(--text-muted)] font-mono ml-auto">
                      {paragraphResult.paragraphs.length} paragraphs
                    </span>
                  </div>
                  <ConfidenceHeatmap paragraphResult={paragraphResult} />
                </div>
              )}
            </div>
          )}

          {paragraphResult ? renderParagraphResults(paragraphResult) : renderResult(currentResult, uploadResult ? 'upload' : 'classification')}

          {/* Comparison Results Panel */}
          {compareResults && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Retrieval Mode Comparison</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {compareResults.results.map((r) => (
                  <div key={r.mode} className={`p-4 rounded-lg border ${
                    r.mode === compareResults.comparison.highest_confidence_mode ? 'border-green-500 bg-green-50' : 'border-gray-200'
                  }`}>
                    <div className="text-xs uppercase font-mono text-gray-500 mb-1">{r.mode}</div>
                    <div className="text-lg font-bold">{r.classification || 'ERROR'}</div>
                    <div className="text-sm text-gray-600">Confidence: {((r.confidence || 0) * 100).toFixed(0)}%</div>
                    {r.caveats?.length > 0 && <div className="text-xs mt-1">Caveats: {r.caveats.join(', ')}</div>}
                    {r.scg_rules_triggered?.length > 0 && <div className="text-xs mt-1 text-gray-500">{r.scg_rules_triggered.length} rules cited</div>}
                    {r.error && <div className="text-xs text-red-500 mt-1">{r.error}</div>}
                  </div>
                ))}
              </div>
              {!compareResults.comparison.all_agree && (
                <div className="mt-2 text-sm text-amber-600">
                  Modes disagree: {compareResults.comparison.divergences.join('; ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Comparison Results - show even without main result */}
      {!currentResult && compareResults && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3">Retrieval Mode Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {compareResults.results.map((r) => (
              <div key={r.mode} className={`p-4 rounded-lg border ${
                r.mode === compareResults.comparison.highest_confidence_mode ? 'border-green-500 bg-green-50' : 'border-gray-200'
              }`}>
                <div className="text-xs uppercase font-mono text-gray-500 mb-1">{r.mode}</div>
                <div className="text-lg font-bold">{r.classification || 'ERROR'}</div>
                <div className="text-sm text-gray-600">Confidence: {((r.confidence || 0) * 100).toFixed(0)}%</div>
                {r.caveats?.length > 0 && <div className="text-xs mt-1">Caveats: {r.caveats.join(', ')}</div>}
                {r.scg_rules_triggered?.length > 0 && <div className="text-xs mt-1 text-gray-500">{r.scg_rules_triggered.length} rules cited</div>}
                {r.error && <div className="text-xs text-red-500 mt-1">{r.error}</div>}
              </div>
            ))}
          </div>
          {!compareResults.comparison.all_agree && (
            <div className="mt-2 text-sm text-amber-600">
              Modes disagree: {compareResults.comparison.divergences.join('; ')}
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex gap-1" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('text')}
            className={`folder-tab ${activeTab === 'text' ? 'active' : ''}`}
          >
            Text Classification
          </button>
          <button
            onClick={() => setActiveTab('file')}
            className={`folder-tab ${activeTab === 'file' ? 'active' : ''}`}
          >
            Document Upload
          </button>
          <button
            onClick={() => setActiveTab('mosaic')}
            className={`folder-tab flex items-center gap-1.5 ${activeTab === 'mosaic' ? 'active' : ''}`}
          >
            <Squares2X2Icon className="w-3.5 h-3.5" />
            Mosaic Detection
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`folder-tab flex items-center gap-1.5 ${activeTab === 'bulk' ? 'active' : ''}`}
          >
            <ArrowUpTrayIcon className="w-3.5 h-3.5" />
            Bulk Upload
          </button>
        </nav>
      </div>

      {/* Text Classification Tab */}
      {activeTab === 'text' && (
        <div className="vault-card p-6">
          <form onSubmit={handleTextClassification}>
            <div className="mb-4">
              <label htmlFor="text-input" className="block text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
                Enter text to classify
              </label>
              <textarea
                id="text-input"
                rows={8}
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                className="document-input w-full rounded"
                placeholder="Paste or type the document content here..."
              />
            </div>
            
            <div className="mb-4 space-y-3">
              {/* Adversarial Self-Test toggle */}
              <div className="pl-4 border-l-2 border-[var(--spotlight-gold)] space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useAdversarialTest}
                    onChange={(e) => setUseAdversarialTest(e.target.checked)}
                    className="rounded border-gray-300 text-yellow-600 shadow-sm focus:border-yellow-300 focus:ring focus:ring-yellow-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Adversarial Self-Test
                  </span>
                </label>
                <p className="ml-6 text-xs text-gray-500">
                  After classification, runs a counter-argument pass to identify weak points and calibrate confidence
                </p>
              </div>

              {/* SCG Retrieval Mode - always visible */}
              <div className="pl-4 border-l-2 border-green-200 space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SCG Retrieval Mode
                </label>
                <select
                  value={retrievalMode}
                  onChange={(e) => setRetrievalMode(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="">Enhanced (default)</option>
                  <option value="classic">Classic (fastest, no LLM calls)</option>
                  <option value="agentic">Agentic (best recall, slowest)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Controls how SCG rules are retrieved. Classic uses vector similarity only. Enhanced adds semantic query expansion. Agentic uses multiple LLM strategies for maximum recall.
                </p>
              </div>

              {/* Document Segmentation Options */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-800">Document Segmentation</h4>
                <p className="text-xs text-gray-500 mb-2">
                  Choose how to break down documents for individual paragraph classification
                </p>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useParagraphMode}
                    onChange={(e) => setUseParagraphMode(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Segment Document/Paragraphs with Simple Regex (Faster, less original formatting maintained)
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={useLlmSegmentation}
                    onChange={(e) => setUseLlmSegmentation(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Segment Every Portion of the document Using AI (Bullet Points, Paragraphs, etc)
                  </span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={!textInput.trim() || classifyMutation.isLoading || paragraphMutation.isLoading}
                className="vault-btn vault-btn-primary"
              >
                {(classifyMutation.isLoading || paragraphMutation.isLoading) ? (
                  <>
                    <ClockIcon className="w-4 h-4 animate-spin" />
                    {useParagraphMode ? 'Classifying Paragraphs...' : 'Classifying...'}
                  </>
                ) : (
                  <>
                    <DocumentIcon className="w-4 h-4" />
                    {useParagraphMode ? 'Classify Paragraphs' : 'Classify Text'}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCompare}
                disabled={!textInput.trim() || compareLoading}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
              >
                {compareLoading ? 'Comparing...' : 'Compare Modes'}
              </button>
            </div>
          </form>
          
          {(classifyMutation.isError || paragraphMutation.isError) && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    Classification failed: {
                      classifyMutation.error?.response?.data?.detail ||
                      paragraphMutation.error?.response?.data?.detail ||
                      'Unknown error'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {compareError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{compareError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Results moved to top - removed from here */}
        </div>
      )}

      {/* File Upload Tab */}
      {activeTab === 'file' && (
        <div className="vault-card p-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-primary)] mb-2">Upload Document</h3>
            <p className="text-sm text-[var(--text-muted)]">
              Upload PDF, TXT, JSON, or DOCX files. The system will extract text and classify the document automatically using enhanced AI classification.
            </p>
          </div>
          
          <div className="mb-4 space-y-3">
            {/* Document Segmentation Options for File Upload */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-800">Document Segmentation</h4>
              <p className="text-xs text-gray-500 mb-2">
                Choose how to break down documents for individual paragraph classification
              </p>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useParagraphMode}
                  onChange={(e) => setUseParagraphMode(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Segment Document/Paragraphs with Simple Regex (Faster, less original formatting maintained)
                </span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useLlmSegmentation}
                  onChange={(e) => setUseLlmSegmentation(e.target.checked)}
                  className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Segment Every Portion of the document Using AI (Bullet Points, Paragraphs, etc)
                </span>
              </label>
            </div>

            {/* Adversarial Self-Test toggle for file upload */}
            <div className="pl-4 border-l-2 border-[var(--spotlight-gold)] space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useAdversarialTest}
                  onChange={(e) => setUseAdversarialTest(e.target.checked)}
                  className="rounded border-gray-300 text-yellow-600 shadow-sm focus:border-yellow-300 focus:ring focus:ring-yellow-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Adversarial Self-Test
                </span>
              </label>
              <p className="ml-6 text-xs text-gray-500">
                After classification, runs a counter-argument pass to identify weak points and calibrate confidence
              </p>
            </div>

            {/* SCG Retrieval Mode for file upload */}
            <div className="pl-4 border-l-2 border-green-200 space-y-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SCG Retrieval Mode
              </label>
              <select
                value={retrievalMode}
                onChange={(e) => setRetrievalMode(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="">Enhanced (default)</option>
                <option value="classic">Classic (fastest, no LLM calls)</option>
                <option value="agentic">Agentic (best recall, slowest)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Controls how SCG rules are retrieved. Classic uses vector similarity only. Enhanced adds semantic query expansion. Agentic uses multiple LLM strategies for maximum recall.
              </p>
            </div>
          </div>

          <FileUpload 
            onFileSelect={handleFileUpload}
            accept=".pdf,.txt,.json,.docx"
            className="mb-4"
          />
          
          {uploadMutation.isLoading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <ClockIcon className="w-5 h-5 text-blue-400 animate-spin mr-3" />
                <p className="text-sm text-blue-700">Processing document...</p>
              </div>
            </div>
          )}
          
          {uploadMutation.isError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <ExclamationCircleIcon className="w-5 h-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    Upload failed: {uploadMutation.error?.response?.data?.detail || 'Unknown error'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Results moved to top - removed from here */}
        </div>
      )}

      {/* Mosaic Detection Tab */}
      {activeTab === 'mosaic' && (
        <MosaicDetection embedded={true} />
      )}

      {/* Bulk Upload Tab */}
      {activeTab === 'bulk' && (
        <div className="vault-card p-6">
          <div className="mb-4">
            <label className="block text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
              Select files to classify in bulk
            </label>
            <input
              type="file"
              multiple
              accept=".pdf,.txt,.docx"
              onChange={(e) => {
                setBulkFiles(Array.from(e.target.files));
                setBatchId(null);
                setBatchStatus(null);
              }}
              className="block w-full text-sm text-[var(--text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[var(--bg-tertiary)] file:text-[var(--text-primary)] hover:file:bg-[var(--accent-primary)] hover:file:text-white cursor-pointer"
            />
          </div>

          {bulkFiles.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)] mb-2">
                {bulkFiles.length} file{bulkFiles.length !== 1 ? 's' : ''} selected
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {bulkFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                    <DocumentIcon className="w-4 h-4 text-[var(--text-muted)]" />
                    <span className="truncate">{f.name}</span>
                    <span className="text-xs text-[var(--text-muted)]">({(f.size / 1024).toFixed(1)} KB)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={handleBulkUpload}
            disabled={bulkFiles.length === 0 || bulkUploading}
            className="vault-btn vault-btn-primary w-full mb-6"
          >
            {bulkUploading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Uploading...
              </span>
            ) : (
              'Upload & Classify All'
            )}
          </button>

          {batchStatus?.error && !batchStatus.results && (
            <div className="p-3 rounded bg-red-900/30 border border-red-700/50 text-red-300 text-sm mb-4">
              {batchStatus.error}
            </div>
          )}

          {batchStatus && batchStatus.total_files > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">
                  Results: {batchStatus.completed_files || 0} / {batchStatus.total_files} files
                </p>
                {batchStatus.status === 'processing' && (
                  <span className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)]">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Processing...
                  </span>
                )}
                {batchStatus.status === 'completed' && (
                  <span className="flex items-center gap-1.5 text-xs text-green-400">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    Complete
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-[var(--bg-tertiary)] rounded-full h-1.5 mb-4">
                <div
                  className="bg-[var(--accent-primary)] h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${batchStatus.total_files > 0 ? ((batchStatus.completed_files || 0) / batchStatus.total_files) * 100 : 0}%` }}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-primary)]">
                      <th className="text-left py-2 px-3 text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Filename</th>
                      <th className="text-center py-2 px-3 text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Status</th>
                      <th className="text-center py-2 px-3 text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Classification</th>
                      <th className="text-center py-2 px-3 text-xs font-mono uppercase tracking-wider text-[var(--text-muted)]">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(batchStatus.results || []).map((r, i) => (
                      <tr key={i} className="border-b border-[var(--border-primary)]/30 hover:bg-[var(--bg-tertiary)]/50">
                        <td className="py-2 px-3 text-[var(--text-secondary)] truncate max-w-xs">
                          <div className="flex items-center gap-2">
                            <DocumentIcon className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" />
                            {r.filename}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-center">
                          {(r.status === 'pending' || r.status === 'processing') && (
                            <svg className="animate-spin h-4 w-4 mx-auto text-[var(--accent-primary)]" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          )}
                          {r.status === 'completed' && (
                            <CheckCircleIcon className="w-5 h-5 mx-auto text-green-400" />
                          )}
                          {r.status === 'failed' && (
                            <XCircleIcon className="w-5 h-5 mx-auto text-red-400" title={r.error || 'Classification failed'} />
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {r.classification ? (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${
                              r.classification.toUpperCase().includes('TOP SECRET') ? 'bg-red-900/50 text-red-300 border border-red-700/50' :
                              r.classification.toUpperCase().includes('SECRET') ? 'bg-orange-900/50 text-orange-300 border border-orange-700/50' :
                              r.classification.toUpperCase().includes('CONFIDENTIAL') ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700/50' :
                              'bg-green-900/50 text-green-300 border border-green-700/50'
                            }`}>
                              {r.classification}
                            </span>
                          ) : (
                            <span className="text-[var(--text-muted)]">--</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center text-[var(--text-secondary)]">
                          {r.confidence != null ? `${(r.confidence * 100).toFixed(0)}%` : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}